import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';

/**
 * Module 4 — A4.6 "Today's 1-on-1" planner.
 *
 * Consolidated triage list to add/remove creators from today's 1-on-1 list
 * (`creator_current_month.is_in_today_1on1_list`, auto-cleared nightly — D-032).
 * Sort by risk / upside / tier.
 *
 * Two ways to add a creator to the list, both call the parent's `onToggle`
 * (which invokes `set_creator_1on1` RPC + logs to Module 6):
 *  - **Tap** anywhere on a row → toggles (works in both directions, incl. remove).
 *  - **Swipe right** on a not-in-list row → reveals a green "+ Add to list"
 *    panel underneath, commits add on release past threshold. Matches mockup
 *    A4.6 (Reco_TCN/mockup-extracted/module4-creators.jsx:298-328). Disabled
 *    on already-in-list rows by design — mockup never shows a "remove" panel.
 *  - **RTL** (AR) inverts the gesture: swipe left reveals the panel on the right.
 *
 * Props:
 *   - creators: full roster (clear-text fields only; no decryption needed here).
 *   - onToggle: (creator, next:boolean) => void
 *   - busyId: creator_id currently mid-toggle (disables its row)
 */

// Mockup A4.6 specs (module4-creators.jsx:300-313):
//   action panel: full inset, green TCN.green, paddingLeft 18, borderRadius 12
//   row when revealed: transform: translateX(110px), transition: transform .3s
// Commit threshold ≈ half of the reveal distance — past that, release = action.
const SWIPE_REVEAL_PX = 110;
const SWIPE_COMMIT_PX = 60;
// Vertical-scroll guard: ignore the gesture if vertical movement dominates
// horizontal by more than this slack. Prevents stealing the list's scroll.
const SWIPE_DOM_SLACK = 6;
const ALERT_RANK = { critical: 3, at_risk: 2, none: 1 };
const UPSIDE_RANK = { high_upside: 3, balanced: 2, high_risk: 1, low_priority: 0 };

const scoreFor = (c, t) => {
  if (c.alert_status === 'critical')
    return { label: t('tcn.m4.alert.critical', 'Critical'), color: TCN.red };
  if (c.alert_status === 'at_risk')
    return { label: t('tcn.m4.alert.at_risk', 'At risk'), color: TCN.amber };
  if (c.priority_category === 'high_upside')
    return { label: t('tcn.m4.priority.high_upside', 'High upside'), color: TCN.green };
  return { label: t('tcn.m4.planner.onPace', 'On pace'), color: TCN.textDim };
};

const SORTS = [
  { id: 'risk', label: 'Risk first' },
  { id: 'upside', label: 'Upside' },
  { id: 'tier', label: 'Tier' },
];

/**
 * Inline swipe-to-action wrapper for a single row.
 *
 * Mockup A4.6 affordance: the user drags the row horizontally to reveal a
 * green "+ Add to list" panel underneath, releasing past threshold fires the
 * action. We:
 *   - intercept touch on the foreground row only;
 *   - watch for vertical-dominant motion → bail so the list keeps scrolling;
 *   - clamp the drag to [0, SWIPE_REVEAL_PX] in the active direction;
 *   - on release ≥ SWIPE_COMMIT_PX: animate to full reveal, fire onAction,
 *     then snap back to 0 (no inline "open drawer" persistent state).
 *
 * RTL: invert the gesture sign (swipe left in AR), panel sits on the right.
 *
 * Tap pass-through: if the user didn't move horizontally past a few px we
 * still call onTap on release — keeps the legacy "tap to toggle" working
 * without conflicting with the swipe gesture.
 *
 * Disabled / busy: skip all handlers, the row is still tappable as a fallback.
 */
const SwipeRow = ({ enabled, isRtl, busy, onTap, onAction, actionLabel, actionAccent, children }) => {
  const [x, setX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef(null);
  const movedRef = useRef(false);
  const sign = isRtl ? -1 : 1;

  const reset = () => {
    setX(0);
    setDragging(false);
    startRef.current = null;
  };

  const handleTouchStart = (e) => {
    if (!enabled || busy) return;
    const t = e.touches[0];
    startRef.current = { x: t.clientX, y: t.clientY };
    movedRef.current = false;
    setDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!startRef.current) return;
    const t = e.touches[0];
    const dxRaw = (t.clientX - startRef.current.x) * sign;
    const dy = Math.abs(t.clientY - startRef.current.y);
    // Vertical-scroll guard — let the parent list scroll instead of stealing it.
    if (!movedRef.current && dy > Math.abs(dxRaw) + SWIPE_DOM_SLACK) {
      reset();
      return;
    }
    if (Math.abs(dxRaw) > 4) movedRef.current = true;
    const clamped = Math.max(0, Math.min(SWIPE_REVEAL_PX, dxRaw));
    setX(clamped);
  };

  const handleTouchEnd = () => {
    if (!startRef.current) return;
    const committed = x >= SWIPE_COMMIT_PX;
    if (committed) {
      // Animate to full reveal, then fire + snap back. Matches mockup's
      // .3s transition; the user sees the action panel fully exposed before
      // the row resets and the chip flips to "★ Today".
      setX(SWIPE_REVEAL_PX);
      setDragging(false);
      window.setTimeout(() => {
        setX(0);
        onAction?.();
      }, 180);
    } else {
      reset();
    }
    startRef.current = null;
  };

  const handleClick = (e) => {
    if (movedRef.current) {
      // Suppress the synthetic click that follows a horizontal drag.
      e.preventDefault();
      e.stopPropagation();
      movedRef.current = false;
      return;
    }
    onTap?.();
  };

  return (
    <div
      style={{
        position: 'relative',
        // Clip the translated row so its 110px slide doesn't push the page into
        // horizontal scroll at 375px (page content is ~347px, row width = full,
        // translating 110px right would otherwise breach the viewport). The
        // action panel sits inside this clip and stays fully revealed.
        overflow: 'hidden',
        borderRadius: 12,
      }}
    >
      {/* Action panel underneath — revealed by the row translating away. */}
      {enabled && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: actionAccent,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: isRtl ? 'flex-end' : 'flex-start',
            paddingLeft: isRtl ? 0 : 18,
            paddingRight: isRtl ? 18 : 0,
            color: '#000',
            fontWeight: 700,
            fontSize: 13,
            gap: 6,
            // Hide the panel chrome when at rest — avoids a sliver poking
            // out from the row border when no swipe is active.
            opacity: x > 0 ? 1 : 0,
            pointerEvents: 'none',
          }}
        >
          <Icon name="plus" size={14} color="#000" />
          {actionLabel}
        </div>
      )}
      {/* Foreground row — only this captures the touch + click. */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={reset}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !busy) {
            e.preventDefault();
            onTap?.();
          }
        }}
        style={{
          transform: `translateX(${x * sign}px)`,
          transition: dragging ? 'none' : 'transform 0.3s',
          // Tell the browser we only handle vertical pan natively — horizontal
          // belongs to our gesture. Prevents Safari rubber-banding the page.
          touchAction: 'pan-y',
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.6 : 1,
          // Prevent text/avatar selection while dragging.
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
};

const OneOnOnePlanner = ({ creators = [], onToggle, busyId = null }) => {
  const { t, i18n } = useTranslation();
  const [sort, setSort] = useState('risk');
  // RTL flag — drives SwipeRow gesture direction. i18n.dir() is the source of
  // truth (changes synchronously when the user switches to AR); we also fall
  // back to document.dir for the unusual case of i18n not being initialized.
  const isRtl = useMemo(() => {
    const dir = i18n?.dir ? i18n.dir() : null;
    if (dir === 'rtl' || dir === 'ltr') return dir === 'rtl';
    if (typeof document !== 'undefined') return document.documentElement.dir === 'rtl';
    return false;
  }, [i18n, i18n?.language]);

  const inListCount = useMemo(
    () => creators.filter((c) => c.is_in_today_1on1_list).length,
    [creators],
  );

  const sorted = useMemo(() => {
    const rows = [...creators];
    if (sort === 'risk') {
      rows.sort(
        (a, b) =>
          (ALERT_RANK[b.alert_status] || 0) - (ALERT_RANK[a.alert_status] || 0) ||
          (b.tier_current || 0) - (a.tier_current || 0),
      );
    } else if (sort === 'upside') {
      rows.sort(
        (a, b) =>
          (UPSIDE_RANK[b.priority_category] ?? -1) - (UPSIDE_RANK[a.priority_category] ?? -1) ||
          (b.tier_current || 0) - (a.tier_current || 0),
      );
    } else {
      rows.sort((a, b) => (b.tier_current || 0) - (a.tier_current || 0));
    }
    return rows;
  }, [creators, sort]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Pin banner — count + auto-clear note */}
      <div
        style={{
          padding: 12,
          background: `${TCN.cyan}14`,
          border: `1px solid ${TCN.cyan}33`,
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Icon name="pin" size={18} color={TCN.cyan} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TCN.text }}>
            {t('tcn.m4.planner.scheduled', '{{count}} calls scheduled today', {
              count: inListCount,
            })}
          </div>
          <div style={{ fontSize: 11, color: TCN.textDim, marginTop: 2 }}>
            {t('tcn.m4.planner.autoClears', 'Auto-clears at midnight')}
          </div>
        </div>
      </div>

      {/* Swipe hint — mockup A4.6 subtitle ("Swipe right → add to list"). Kept
          as a small caption above the chips because the page TopBar is shared
          with the "All creators" mode and we don't want to swap it per-tab. */}
      <div
        style={{
          fontSize: 11,
          color: TCN.textDim,
          textAlign: 'center',
          marginTop: -4,
          fontStyle: 'italic',
        }}
      >
        {t('tcn.m4.planner.swipeHint', 'Swipe right → add to list')}
      </div>

      {/* Sort chips */}
      <div style={{ display: 'flex', gap: 6 }}>
        {SORTS.map((s) => {
          const active = sort === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setSort(s.id)}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 600,
                background: active ? TCN.surface3 : TCN.surface1,
                border: `1px solid ${active ? TCN.cyan : TCN.border}`,
                color: active ? TCN.text : TCN.textDim,
              }}
            >
              ↕ {t(`tcn.m4.planner.sort.${s.id}`, s.label)}
            </button>
          );
        })}
      </div>

      {/* Rows — swipe-right reveals "+ Add to list" on not-in-list rows; tap
          still toggles (works in both directions for backward compat). */}
      {sorted.map((c) => {
        const added = !!c.is_in_today_1on1_list;
        const score = scoreFor(c, t);
        const busy = busyId === c.creator_id;
        const handle = c.tiktok_username || c.display_name || c.creator_id;
        return (
          <SwipeRow
            key={c.creator_id}
            enabled={!added}
            isRtl={isRtl}
            busy={busy}
            onTap={() => !busy && onToggle?.(c, !added)}
            onAction={() => !busy && onToggle?.(c, true)}
            actionLabel={t('tcn.m4.planner.swipeAdd', 'Add to list')}
            actionAccent={TCN.green}
          >
            <div
              style={{
                padding: 12,
                background: TCN.surface1,
                border: `1px solid ${added ? `${TCN.cyan}55` : TCN.border}`,
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontFamily: 'inherit',
              }}
            >
              <Avatar name={c.display_name || c.tiktok_username || ''} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: TCN.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  @{handle}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    marginTop: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <span style={{ color: TCN.textMute }}>
                    {c.tier_current ? `T${c.tier_current}` : '—'}
                  </span>
                  <span style={{ color: TCN.textMute }}>·</span>
                  <span style={{ color: score.color, fontWeight: 600 }}>{score.label}</span>
                </div>
              </div>
              {added ? (
                <Chip color="cyan" solid size="sm">
                  ★ {t('tcn.m4.planner.today', 'Today')}
                </Chip>
              ) : (
                <Icon name="plus" size={16} color={TCN.textMute} />
              )}
            </div>
          </SwipeRow>
        );
      })}

      {sorted.length === 0 && (
        <div style={{ textAlign: 'center', padding: 24, color: TCN.textDim, fontSize: 13 }}>
          {t('tcn.m4.planner.empty', 'No creators to plan.')}
        </div>
      )}
    </div>
  );
};

export default OneOnOnePlanner;
